import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isFileObject } from "../../utils/validation";
import { message } from "antd";
import { DocumentService } from "../../services/documentService";
import { DocCategoryService } from "../../services/docCategoryService";
import { DocCategoryDetail } from "../DocumentCategoryContext/constants";
import { DocumentAddData, DocumentUpdateData, DocumentListItem, DocumentDetail, defaultDocumentAddData, defaultDocumentUpdateData, OperationStatus } from "./constants";

interface DocumentContextState {
    // Core Domain Data
    documentList: DocumentListItem[];
	documentForm: DocumentAddData;
	docCategoryDetail: DocCategoryDetail | null;
	documentDetail: DocumentDetail | null;
	documentUpdate: DocumentUpdateData | null;

    operationStatus: OperationStatus;

	// Setters
	setDocumentForm: React.Dispatch<React.SetStateAction<DocumentAddData>>;
	setDocumentUpdate: React.Dispatch<React.SetStateAction<DocumentUpdateData | null>>;
	clearDocumentData: () => void;

    // Actions
    addDocument: () => Promise<void>;
    removeDocument: (categoryId: string, docId: string) => Promise<void>;
	getDocumentList: (categoryId?: string) => Promise<void>;
	getDocumentCategoryDetail: (id: string) => Promise<void>;
	getDocumentDetail: (id: string) => Promise<void>;
	updateDocument: () => Promise<void>;
}

interface DocumentProviderProps {
    children: React.ReactNode;
}

const DocumentContext = createContext<DocumentContextState | undefined>(
	undefined
);

export const useDocumentContext = () => {
	const context = useContext(DocumentContext);
	if (!context) {
		throw new Error("useDocumentContext must be used within a DocumentProvider");
	}
	return context;
};

export const DocumentContextProvider: React.FC<DocumentProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [documentList, setDocumentList] = useState<DocumentListItem[]>([]);
	const [documentForm, setDocumentForm] = useState<DocumentAddData>(defaultDocumentAddData);
	const [docCategoryDetail, setDocCategoryDetail] = useState<DocCategoryDetail | null>(null);
	const [documentDetail, setDocumentDetail] = useState<DocumentDetail | null>(null);
	const [documentUpdate, setDocumentUpdate] = useState<DocumentUpdateData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const isDocumentDataValid = (data: DocumentAddData) => {
		if (!data.categoryId) {
			return false;
		}

		for (const document of data.documents || []) {
			if (!document && !isFileObject(document)) {
				return false;
			}
		}

		return true;
	};

	const clearDocumentData = () => {
		setDocumentForm(defaultDocumentAddData);
	};

	const addDocument = async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isDocumentDataValid(documentForm)) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocumentService.addDocument(documentForm);

			if (response.data?.result?.isSuccessful) {
				message.success("Belge başarıyla eklendi.");
				setDocumentForm(defaultDocumentAddData);
				navigate(`/documents/${docCategoryDetail?.categoryId}`);
			} else {
				message.error(response.data.result?.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const removeDocument = async (categoryId: string, docId: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await DocumentService.removeDocument(docId);
			if (response.data?.result?.isSuccessful) {
				message.success("Belge başarıyla silindi.");
				await getDocumentList(categoryId);
			} else {
				message.error(response.data?.result?.error ? response.data.result.error.message : "Belge silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const getDocumentList = async (categoryId?: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocumentService.getDocumentList(categoryId);

			if (response.isSuccessful) {
				setDocumentList(response.result?.items || []);
				clearDocumentData();
			} else {
				message.error(response.error ? response.error : "Belge listesi yüklenirken hata oluştu.");
				setDocumentList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Belge listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const getDocumentCategoryDetail = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocCategoryService.getDocCategoryDetails(id);

			if (response.isSuccessful) {
				setDocCategoryDetail({
					categoryId: response.result.categoryId,
					categoryNo: response.result.categoryNo,
					name: response.result.name,
				});
				setDocumentForm((prev) => ({
					...prev,
					categoryId: response.result.categoryId
				}));
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const getDocumentDetail = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocumentService.getDocumentDetail(id);

			if (response.isSuccessful) {
				setDocumentDetail(response.result);
				setDocumentUpdate({
					documentId: response.result.id,
					documentName: response.result.name,
				});
			} else {
				message.error(response.error || "Belge detayı yüklenirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const updateDocument = async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!documentUpdate?.documentId || !documentUpdate?.documentName) {
			message.info("Lütfen belge adını girin.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocumentService.updateDocument(documentUpdate);

			if (response.data?.result?.isSuccessful) {
				message.success("Belge başarıyla güncellendi.");
				setDocumentUpdate(defaultDocumentUpdateData);
				navigate(`/documents/${docCategoryDetail?.categoryId}`);
			} else {
				message.error(response.data?.result?.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	return (
		<DocumentContext.Provider
			value={{
				documentList,
				documentForm,
				docCategoryDetail,
				documentDetail,
				documentUpdate,
				setDocumentUpdate,
				setDocumentForm,
				clearDocumentData,
				operationStatus,
				addDocument,
				removeDocument,
				getDocumentCategoryDetail,
				updateDocument,
				getDocumentDetail,
				getDocumentList,
			}}
		>
			{children}
		</DocumentContext.Provider>
	);  
};
